import * as React from 'react';
import { useEffect, useRef, useState } from 'react';
import { Card, Row, Table } from 'react-bootstrap';
import { FaCalendarAlt, FaChartPie, FaClipboardList, FaTimes } from 'react-icons/fa';
import TableLayout from '../../../shared/layout/table/TableLayout';
import { ITableColumn } from '../../../shared/components/TableSkeleton';
import { ErrorToast } from '../../../utils/toasters';
import { ContollingService } from '../ControllingService';
import IncomingDeferralTabs from './IncomingDeferralTabs';
import { IncomingDeferralFilter } from './IncomingDeferralFilter';
import { ControllingCard } from './ControllingCard';
import { IIncomingDeferral } from '../../../model/controlling/Controlling';
import { priceSplitter } from '../../../utils/helpers/NumberFormater';
import { ImpersonateEye } from '../../../shared/components/ImpersonateEye';

export interface IIncomingDeferralStatistics {
    standardDeferral: number;
    longTermDeferral: number;
    totalDeferral: number;
}

const getDeferralColumns = (table: 'offers' | 'contracts'): ITableColumn[] => [
    { label: 'ID' },
    { label: 'Campaign name' },
    { label: 'Influencer name' },
    { label: 'Agency name' },
    ...(table === 'contracts' ? [{ label: 'Position' }] : []),
    { label: 'Total offer value' },
];

export function IncomingDeferral() {
    const [dataLoading, setDataLoading] = useState(true);
    const [incomingDeferrals, setIncomingDeferrals] = useState<IIncomingDeferral[]>([]);
    const [staticts, seuStaticts] = useState<IIncomingDeferralStatistics | null>(null);
    const [table, setTable] = useState<'offers' | 'contracts'>('offers');
    const [filters, setFilters] = useState({
        page: 1,
        perPage: 15,
        id: '',
        totalPages: 0,
        campaignId: undefined,
        agencyId: undefined,
        influencerId: undefined,
    });
    const requestIdRef = useRef(0);
    const columns = getDeferralColumns(table);
    const isStatsLoading = dataLoading && !staticts;

    useEffect(() => {
        setFilters(prev => ({ ...prev, page: 1 }));
        fetchIncomingDeferrals({ ...filters, page: 1 });
    }, [table]);

    const normalizeIncomingDeferrals = (data: any[], type: 'offers' | 'contracts'): IIncomingDeferral[] => {
        return data.map(item => {
            if (type === 'offers') return item;
            return {
                id: item.id,
                createdAt: item.createdAt,
                status: item.status,
                sequenceNumber: item?.sequenceNumber,
                price: item.price,
                isCurrent: item.isCurrent ?? false,
                acceptedDate: item.acceptedDate ?? null,
                isLongTerm: false,
                isAddonsOnly: false,
                hasInfluencerFee: false,
                influencerFeePercentage: null,
                campaign: item.contract?.campaign ?? null,
                user: item.contract?.user ?? null,
                contract: item.contract ?? null,
                counterOfferBy: null,
                acceptedBy: null,
                buttonType: 'Open',
                totalPlatforms: [],
                possibleInvoices: item?.contract?.possibleInvoices,
            };
        });
    };

    const fetchIncomingDeferrals = (params: typeof filters, pageOverride?: number) => {
        const requestId = ++requestIdRef.current;
        setDataLoading(true);
        const page = pageOverride ?? params.page;

        ContollingService.getControlling({ ...params, type: table, page })
            .then(({ data, page: responsePage, count, info }: any) => {
                if (requestId !== requestIdRef.current) return;

                const totalPages = Math.ceil(count / params.perPage);
                if (totalPages > 0 && page > totalPages) {
                    fetchIncomingDeferrals({ ...params, page: 1 });
                    return;
                }
                const normalizedData = normalizeIncomingDeferrals(data, table);
                setIncomingDeferrals(normalizedData);
                seuStaticts(info);
                setFilters(prev => ({
                    ...prev,
                    page: responsePage,
                    totalPages,
                }));
            })
            .catch(err => {
                if (requestId === requestIdRef.current) ErrorToast(err);
            })
            .finally(() => {
                if (requestId === requestIdRef.current) setDataLoading(false);
            });
    };

    const handlePageChange = (page: number) => {
        fetchIncomingDeferrals(filters, page);
    };

    return (
        <>
            <div className="mb-5">
                <Row className="gap-y-4">
                    <ControllingCard
                        cardName="Standard Deferral"
                        icon={<FaClipboardList />}
                        baseColor="primary"
                        total={staticts?.standardDeferral ?? 0}
                        styleCard="h-100"
                        isLoading={isStatsLoading}
                    />
                    <ControllingCard
                        cardName="Long Term Deferral"
                        icon={<FaCalendarAlt />}
                        baseColor="primary"
                        total={staticts?.longTermDeferral ?? 0}
                        styleCard="h-100"
                        isLoading={isStatsLoading}
                    />
                    <ControllingCard
                        cardName="Total Deferral"
                        icon={<FaChartPie />}
                        baseColor="primary"
                        total={staticts?.totalDeferral ?? 0}
                        styleCard="h-100"
                        isLoading={isStatsLoading}
                    />
                </Row>
            </div>

            <Card
                className="mb-5 p-4"
                onKeyDown={(e: React.KeyboardEvent) => {
                    if (e.key === 'Enter') fetchIncomingDeferrals(filters);
                }}
            >
                <IncomingDeferralFilter
                    handleState={fetchIncomingDeferrals}
                    setState={setFilters}
                    offersFilter={filters}
                />
            </Card>
            <IncomingDeferralTabs table={table} setTable={setTable} />
            <TableLayout
                pagination={filters}
                listLength={incomingDeferrals.length}
                handlePages={handlePageChange}
                isLoading={dataLoading}
                dataName={table}
                skeletonColumns={columns}
                skeletonRows={filters.perPage}
            >
                <Table hover responsive>
                    <thead>
                        <tr>
                            {columns.map(column => (
                                <th key={column.label} className={column.className}>
                                    {column.label}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {incomingDeferrals.map(incomingDeferral => (
                            <tr key={incomingDeferral.id}>
                                <td>{incomingDeferral?.id}</td>
                                <td>
                                    {incomingDeferral?.campaign?.title ? (
                                        <>
                                            <span className="text-primary">({incomingDeferral.campaign.id})</span>{' '}
                                            {incomingDeferral.campaign.title}
                                        </>
                                    ) : (
                                        <span className="word-break text-danger">
                                            <FaTimes />
                                        </span>
                                    )}
                                </td>
                                <td>
                                    <ImpersonateEye id={incomingDeferral?.user?.id ?? 0} />
                                    {incomingDeferral?.user?.displayName}
                                    <br />
                                    {incomingDeferral?.user?.firstName ?? '-'} {incomingDeferral?.user?.lastName}
                                </td>
                                <td>
                                    {incomingDeferral?.user?.agencyCompany?.displayName ? (
                                        <span className="word-break">
                                            <span className="text-primary">
                                                ({incomingDeferral?.user?.agencyCompany?.id})
                                            </span>{' '}
                                            {incomingDeferral?.user?.agencyCompany?.displayName}
                                        </span>
                                    ) : (
                                        <span className="word-break text-danger">
                                            <FaTimes />
                                        </span>
                                    )}
                                </td>
                                {table === 'contracts' && (
                                    <td>
                                        {(incomingDeferral?.sequenceNumber ?? 0) +
                                            '/' +
                                            (incomingDeferral?.possibleInvoices ?? 0)}
                                    </td>
                                )}
                                <td>{priceSplitter(incomingDeferral?.price)} €</td>
                            </tr>
                        ))}
                    </tbody>
                </Table>
            </TableLayout>
        </>
    );
}
